import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { LogWarning } from 'utils/loggingHelpers';
import { PREVIEW_BANNER_KEYS } from './previewBannerCopy';
import styles from './SurveyPreviewBanner.module.scss';

const COPIED_NOTICE_DURATION_MS = 3000;

interface Props {
  shareUrl: string;
  /** The formatted expiry of the link being copied, once known. */
  expiry?: string;
}

type CopyState = 'idle' | 'copied' | 'failed';

const writeToClipboard = async (text: string): Promise<boolean> => {
  if (typeof ClipboardItem === 'undefined' || !navigator?.clipboard?.write) {
    LogWarning('Clipboard API unavailable, cannot copy preview link');
    return false;
  }
  const type = 'text/plain';
  try {
    await navigator.clipboard.write([new ClipboardItem({ [type]: new Blob([text], { type }) })]);
    return true;
  } catch (error) {
    LogWarning('Failed to copy preview link to clipboard', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Copies the share link. The live region is rendered empty up front and only its text changes,
 * because screen readers skip a region that arrives already filled. When copying fails, the link
 * is shown selected so it can be copied by hand.
 */
export function PreviewShareControl({ shareUrl, expiry }: Props) {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  const fallbackInput = useRef<HTMLInputElement>(null);

  useEffect(() => () => clearTimeout(resetTimer.current), []);
  useEffect(() => {
    if (copyState === 'failed') {
      fallbackInput.current?.select();
    }
  }, [copyState]);

  const handleCopy = async () => {
    clearTimeout(resetTimer.current);
    const copied = await writeToClipboard(shareUrl);
    setCopyState(copied ? 'copied' : 'failed');
    if (copied) {
      resetTimer.current = setTimeout(() => setCopyState('idle'), COPIED_NOTICE_DURATION_MS);
    }
  };

  const statusText = {
    idle: '',
    copied: expiry ? t(PREVIEW_BANNER_KEYS.linkCopied, { date: expiry }) : t(PREVIEW_BANNER_KEYS.copied),
    failed: t(PREVIEW_BANNER_KEYS.copyFailed),
  }[copyState];

  return (
    <div data-testid="share-preview-button" className={styles.share}>
      <ActionButton onClick={handleCopy} type={ButtonType.SYSTEM}>
        <Icon name="share" color="currentColor" ariaHidden /> {t(PREVIEW_BANNER_KEYS.copyLink)}
      </ActionButton>
      <div role="status" className={statusText && copyState === 'copied' ? styles.copied : undefined}>
        {statusText}
      </div>
      {copyState === 'failed' && (
        <input
          ref={fallbackInput}
          className={styles.fallbackLink}
          readOnly
          value={shareUrl}
          aria-label={t(PREVIEW_BANNER_KEYS.copyLink)}
        />
      )}
    </div>
  );
}
